import type { Comic } from '@tanko/shared'
import type { Candidate } from './fs-access'

export type RelinkEligibility = { ok: true } | { ok: false; reason: string }

const kindLabel = (kind: Comic['kind']) => kind === 'archive' ? '壓縮檔' : '資料夾'

/**
 * 一個候選能不能接到這本找不到檔案的作品上。
 *
 * 理由互斥且依序判定：已加入書庫、掃描錯誤、形態不同、頁數不同。前兩者是候選自己的狀態，
 * 與這本作品無關；後兩者是「這不是同一本」的判準——搬動或改名不會改變頁數與形態，頁數
 * 不同的候選接上去會讓雲端目錄的頁數對不上、進度與書籤的頁碼失去意義。
 *
 * 不做自動配對（產品原則四）：這裡只說每一個候選為什麼不行，選哪一個由使用者決定。
 */
export function relinkEligibility(comic: Pick<Comic, 'pageCount' | 'kind'>, candidate: Candidate): RelinkEligibility {
  if (candidate.added) return { ok: false, reason: '已加入書庫' }
  if (candidate.error) return { ok: false, reason: candidate.error }
  if (candidate.kind !== comic.kind) return { ok: false, reason: `形態不同 · 這本是${kindLabel(comic.kind)}` }
  if (candidate.pageCount !== comic.pageCount) {
    return { ok: false, reason: `${candidate.pageCount} 頁 · 與這本作品的 ${comic.pageCount} 頁不同` }
  }
  return { ok: true }
}

export interface RelocationCounts { found: number; total: number; missingTitles: readonly string[] }

/** 確認對話框最多逐一列出幾個找不到的標題，其餘以本數帶過。 */
export const RELOCATION_LISTED_TITLES = 10

/**
 * 來源重新指向的確認文案：找到幾本、找不到哪些。
 *
 * 找到 0 本仍然可以確認——換過去之後全部仍是遺失，沒有東西會壞——但要明講，並提醒可能選錯。
 */
export function relocationSummary(preview: RelocationCounts): string {
  if (!preview.total) return '這個來源底下目前沒有作品，只會更換它指向的資料夾。'
  const found = `在新位置找到 ${preview.found}／${preview.total} 本作品。`
  if (!preview.missingTitles.length) return `${found}確認後底下的作品會從這裡讀取；識別碼、分類、進度與書籤都不變。`
  const listed = preview.missingTitles.slice(0, RELOCATION_LISTED_TITLES).map(title => `「${title}」`).join('、')
  const rest = preview.missingTitles.length > RELOCATION_LISTED_TITLES
    ? `（還有 ${preview.missingTitles.length - RELOCATION_LISTED_TITLES} 本）`
    : ''
  const tail = preview.found
    ? '確認後找不到的那些仍會標為找不到檔案，資料不會遺失。'
    : '確認後它們仍會標為找不到檔案，資料不會遺失——若是選錯了資料夾，請取消再選一次。'
  return `${found}找不到：${listed}${rest}。${tail}`
}
